import {
  Fit,
  Layout,
  useRive,
  useViewModelInstanceNumber,
} from '@rive-app/react-canvas'
import { AnimatePresence } from 'motion/react'
import { Dialog } from 'radix-ui'
import { useEffect, useState } from 'react'
import { useTrialEligibility } from '~/hooks/use-trial-eligibility'
import { getLocalizedMessage } from '~/lib/l10n'
import { motion } from '~/lib/motion'
import { m } from '~/paraglide/messages'
import { $config } from '~/store/config'
import type { ItemContent } from '~/types/delivery'
import { CloseButton } from './close-button'
import { Spinner } from '@forest/shared/react'

export function FinalRewardPreviewDialog(props: {
  reward: 'free' | 'paid'
  titleKey: string | null
  contentType: ItemContent['type'] | null
  isOpen: boolean
  isClaimed: boolean
  isClaimable: boolean
  isUnlocked: boolean
  onClaim: () => void
  onPurchase: () => void
  onClose: () => void
}) {
  const title = getLocalizedMessage(props.titleKey)
  const { isTrial } = useTrialEligibility()
  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={(nextIsOpen) => !nextIsOpen && props.onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          asChild
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div
            className="gradient-outline fixed left-1/2 top-1/2 z-50 w-[calc(100vw-48px)] max-w-[345px] -translate-x-1/2 -translate-y-1/2 rounded-[32px] outline-none backdrop-blur-md [--ow:2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{
              ['--tw-enter-translate-x' as any]: '-50%',
              ['--tw-enter-translate-y' as any]: '-50%',
              ['--tw-exit-translate-x' as any]: '-50%',
              ['--tw-exit-translate-y' as any]: '-50%',
              willChange: 'backdrop-filter',
              background: '#FFFFFFCC',
            }}
          >
            <Dialog.Close asChild>
              <CloseButton className="absolute -right-2 -top-2" />
            </Dialog.Close>

            <div className="p-6 text-center">
              <div className="h-[252px] w-full">
                <RiveBox reward={props.reward} />
              </div>

              <Dialog.DialogTitle className="mt-4 text-lg font-semibold text-[#333]">
                {title ?? ''}
              </Dialog.DialogTitle>

              <Dialog.DialogDescription className="mt-3 text-[#666]">
                {/* Only land skins have a generic description copy so far;
                    tree types wait on a what_is_tree_type Lokalise key. */}
                {props.contentType === 'land_skin'
                  ? m.what_is_land_skin_description()
                  : ''}
              </Dialog.DialogDescription>

              {!props.isClaimed &&
                (props.reward === 'paid' ? (
                  props.isClaimable || props.isUnlocked ? (
                    <button
                      className="mt-6 h-[52px] w-full rounded-xl bg-white font-bold text-[#176371] disabled:opacity-30"
                      disabled={!props.isClaimable}
                      onClick={() => props.onClaim()}
                    >
                      {m.cart_claim_active()}
                    </button>
                  ) : (
                    <button
                      className="group mt-6 h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
                      onClick={() => props.onPurchase()}
                    >
                      <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
                        {isTrial ? m.btn_try_plus_free() : m.btn_unlock_plus()}
                      </span>
                    </button>
                  )
                ) : (
                  <button
                    className="mt-6 h-[52px] w-full rounded-xl bg-white font-bold text-[#176371] disabled:opacity-30"
                    disabled={!props.isClaimable}
                    onClick={() => props.onClaim()}
                  >
                    {m.cart_claim_active()}
                  </button>
                ))}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function RiveBox(props: { reward: 'free' | 'paid' }) {
  const [isRiveLoaded, setIsRiveLoaded] = useState(false)
  const { rive, RiveComponent } = useRive({
    src: $config.get()!.final_rewards_rive_url,
    artboard: 'final_reward',
    stateMachines: ['State Machine 1'],
    layout: new Layout({
      fit: Fit.FitHeight,
    }),
    autoBind: true,
    autoplay: true,
    onLoad: () => {
      setIsRiveLoaded(true)
    },
  })

  const rewardNumInput = useViewModelInstanceNumber(
    'rewardNum',
    rive?.viewModelInstance,
  )

  useEffect(() => {
    rewardNumInput.setValue(props.reward === 'free' ? 1 : 2)
  }, [rewardNumInput, props.reward])

  return (
    <div className="relative size-full">
      <motion.div
        className="size-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: isRiveLoaded ? 1 : 0 }}
      >
        <RiveComponent className="size-full" />
      </motion.div>

      <AnimatePresence>
        {!isRiveLoaded && (
          <motion.div
            className="absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 transform"
            exit={{ opacity: 0 }}
          >
            <Spinner className="size-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
